import type { LatLng } from '~/_shared/types/latLng';

const DEG_TO_RAD = Math.PI / 180;
const RAD_TO_DEG = 180 / Math.PI;

export const getWeightedCenter = (points: Array<LatLng & { weight?: number }>): LatLng | null => {
  if (points.length === 0) {
    return null;
  }

  let latSum = 0;
  let lngSum = 0;
  let weightSum = 0;
  let minLng = Infinity;
  let maxLng = -Infinity;

  for (const point of points) {
    const weight = point.weight ?? 1;

    latSum += point.lat * weight;
    lngSum += point.lng * weight;
    weightSum += weight;

    if (point.lng < minLng) {
      minLng = point.lng;
    }
    if (point.lng > maxLng) {
      maxLng = point.lng;
    }
  }

  // All weights are explicitly zero — fall back to the unweighted centroid to avoid division by zero
  const allWeightsZero = weightSum === 0;
  if (allWeightsZero) {
    latSum = 0;
    lngSum = 0;
    for (const point of points) {
      latSum += point.lat;
      lngSum += point.lng;
    }
    weightSum = points.length;
  }

  const lat = latSum / weightSum;

  // A longitude span ≤180° cannot straddle the antimeridian, so the planar mean is correct
  if (maxLng - minLng <= 180) {
    return { lat, lng: lngSum / weightSum };
  }

  // Possible antimeridian crossing — use the weighted circular mean of longitudes instead
  let sinSum = 0;
  let cosSum = 0;
  for (const point of points) {
    const weight = allWeightsZero ? 1 : (point.weight ?? 1);
    const lngRad = point.lng * DEG_TO_RAD;

    sinSum += Math.sin(lngRad) * weight;
    cosSum += Math.cos(lngRad) * weight;
  }

  return { lat, lng: Math.atan2(sinSum, cosSum) * RAD_TO_DEG };
};
